import { useEffect, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import proposalsService from '../../services/proposals.service';
import authService from '../../services/auth.service';
import fundService from '../../services/fund.service';
import PageHeader from '../../components/layout/PageHeader';
import catalogService, {
  type OfferingDocumentSummary,
  type ProductDetail as Product,
} from '../../services/catalog.service';
import callbacksService from '../../services/callbacks.service';
import contractsService from '../../services/contracts.service';
import suitabilityService, {
  type SuitabilityCheckResult,
} from '../../services/suitability.service';
import productKycService, {
  type ProductKycCheckResult,
} from '../../services/productKyc.service';
import { getApiErrorCode, getApiErrorMessage } from '../../lib/apiError';
import { CALL_CONTEXT, channelsToExecutive, seriesCallContext } from '../../lib/executiveRefusal';
import ExecutiveRefusal from '../../components/client/ExecutiveRefusal';
import RequestCallButton from '../../components/client/RequestCallButton';
import { isTaxFormRequired, taxFormPath } from '../../lib/taxForm';
import {
  formatEarlyWithdrawal,
  formatManagementFee,
  formatRedemption,
  formatTerm,
} from '../../lib/redemptionTerms';
import AccreditationModal from '../../components/client/AccreditationModal';
import OfferingNotice from '../../components/OfferingNotice';
import RiskDisclaimer from '../../components/RiskDisclaimer';
import { useClientApprovalGate } from '../../hooks/useCurrentUserProfile';
import { useMembershipApproved } from '../../hooks/useMembershipApproved';
import MembershipFirstNote from '../../components/client/MembershipFirstNote';
import { colors, gradients } from '../../styles/theme';
import { RISK_LEVELS } from '../../lib/riskLevels';
import { formatCents } from '../../lib/amount';

/**
 * Client-facing product detail page.
 *
 * Reached from the simulator's "View product details" link or by
 * deep-link. Combines a hero (yield + term + risk + min), a long-form
 * description, and the same next-steps panel philosophy as the
 * simulator: primary consultative CTA ("Talk to your advisor"),
 * secondary research action ("Read the prospectus" if available),
 * tertiary "How to fund" link gated on approval.
 *
 * Auth-only page (any role can technically open it, but the route is
 * inside the client tree). Staff opening the URL gets the same view —
 * useful for advisors who want to remind themselves of a product's
 * specs while on a call.
 */

type CallbackUiState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'requested'; advisorName: string | null; alreadyExisted: boolean }
  | { kind: 'error'; message: string };

/** Human-readable advisor name from a callback result, or null if unknown. */
function formatAdvisorName(
  result: { advisor_first_name?: string | null; advisor_last_name?: string | null },
): string | null {
  if (!result.advisor_first_name && !result.advisor_last_name) return null;
  return `${result.advisor_first_name ?? ''} ${result.advisor_last_name ?? ''}`.trim();
}

/** Distinguish the structured suitability rejection (#58) from generic
 *  network/server errors so the user sees actionable copy, not a stack trace. */
function deriveInvestErrorMessage(e: unknown): string {
  const status = (e as { response?: { status?: number } })?.response?.status;
  const detail = (
    e as { response?: { data?: { detail?: { code?: string; missing_requirements?: string[] } } } }
  )?.response?.data?.detail;
  const kycDetail = (
    e as { response?: { data?: { detail?: { code?: string; invalid_categories?: string[] } } } }
  )?.response?.data?.detail;
  if (status === 403 && kycDetail?.code === 'kyc_documents_not_current') {
    const docs = (kycDetail.invalid_categories ?? [])
      .map((c) => productKycService.describeCategory(c))
      .join(' · ');
    return docs
      ? `We need updated documents before you can contribute: ${docs}.`
      : 'We need updated documents before you can contribute.';
  }
  if (status === 403 && detail?.code === 'product_unsuitable') {
    const reasons = (detail.missing_requirements ?? [])
      .map((c) => suitabilityService.describeRequirement(c))
      .join(' · ');
    return reasons
      ? `This series isn't a fit yet — ${reasons.toLowerCase()}.`
      : "This series isn't a fit for your profile yet.";
  }
  if (status === 403 && detail?.code === 'offering_document_not_acknowledged') {
    return 'Please open and acknowledge the offering document above before contributing.';
  }
  return getApiErrorMessage(e, "Couldn't open the contract right now. Please try again.");
}

/** True when the invest attempt was blocked by the Reg D accreditation gate —
 *  the UI answers with the certification modal instead of an error string. */
function isAccreditationRequired(e: unknown): boolean {
  const status = (e as { response?: { status?: number } })?.response?.status;
  const code = (
    e as { response?: { data?: { detail?: { code?: string } } } }
  )?.response?.data?.detail?.code;
  return status === 403 && code === 'accreditation_required';
}

/** Loads the product + its suitability verdict (#58). Redirects to the
 *  dashboard when no productId is present. Suitability failures are
 *  non-blocking — the server-side gate is the real boundary. */
function useProductDetailData(
  productId: string | undefined,
  gate: string,
  navigate: (path: string, opts?: { replace?: boolean }) => void,
): {
  product: Product | null;
  loadError: string | null;
  notFound: boolean;
  suitability: SuitabilityCheckResult | null;
  kycCheck: ProductKycCheckResult | null;
} {
  const [product, setProduct] = useState<Product | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [suitability, setSuitability] = useState<SuitabilityCheckResult | null>(null);
  const [kycCheck, setKycCheck] = useState<ProductKycCheckResult | null>(null);

  useEffect(() => {
    if (!productId) {
      navigate('/client/dashboard', { replace: true });
      return;
    }
    let cancelled = false;
    catalogService
      .getProduct(productId)
      .then((p) => { if (!cancelled) setProduct(p); })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isNotFound(err)) setNotFound(true);
        else setLoadError(getApiErrorMessage(err, 'Could not load this series.'));
      });
    return () => { cancelled = true; };
  }, [productId, navigate]);

  useEffect(() => {
    if (!productId || gate !== 'approved') return;
    let cancelled = false;
    suitabilityService
      .checkProduct(productId)
      .then((r) => { if (!cancelled) setSuitability(r); })
      .catch(() => { /* leave suitability null — fail-open in UI */ });
    // #59: per-product document checklist — same fail-open posture; the
    // origination gate on POST /contracts/cases is the security boundary.
    productKycService
      .check(productId)
      .then((r) => { if (!cancelled) setKycCheck(r); })
      .catch(() => { /* leave kycCheck null — fail-open in UI */ });
    return () => { cancelled = true; };
  }, [productId, gate]);

  return { product, loadError, notFound, suitability, kycCheck };
}

/** True when the server answered that the Series is not there for this person (404). A Series not
 *  offered in their market, which they have nothing with, reads exactly like one that does not
 *  exist, so the page says one plain thing for both. */
export function isNotFound(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 404;
}

/** Said in place of the page when the Series is not there for this person. */
export function NotAvailableNote(): JSX.Element {
  return (
    <div role="note" className="text-sm" style={{ color: colors.inkSoft, lineHeight: 1.5 }}>
      <p>This series is not available. Your Account Executive can tell you about the series open to you.</p>
      <RequestCallButton context={CALL_CONTEXT.seriesNotAvailable} />
    </div>
  );
}

/** True when the client already holds an active contract for this product — it is
 *  among their fundable products. Adding to it needs no new contract, only a transfer
 *  with their funding reference. Unknown reads as false: the regular path still shows. */
function useHoldsProduct(productId: string | undefined, gate: string): boolean {
  const [holds, setHolds] = useState(false);
  useEffect(() => {
    if (!productId || gate !== 'approved') return;
    let cancelled = false;
    authService
      .getMyId()
      .then((id) => fundService.getFundInstructions(id))
      .then((data) => {
        if (!cancelled) setHolds(data.fundable_products.some((p) => p.product_id === productId));
      })
      .catch(() => { /* leave false — the invest path is still available */ });
    return () => { cancelled = true; };
  }, [productId, gate]);
  return holds;
}

/** "Increase your investment" — for a client who already holds the product. */
function TopUpLink(): JSX.Element {
  return (
    <Link
      to="/client/fund"
      style={{
        display: 'block',
        textAlign: 'center',
        width: '100%',
        padding: '12px 18px',
        borderRadius: 10,
        border: `2px solid ${colors.navy}`,
        fontSize: 14,
        fontWeight: 700,
        background: colors.navy,
        color: '#FFFFFF',
        marginBottom: 12,
        textDecoration: 'none',
      }}
    >
      Add to your holding →
    </Link>
  );
}

/** Whether the client is offered the way to start this Series' contract. Whether the Series is
 *  open to THIS person is the server's decision, read here and never re-decided: `is_active` is
 *  the readiness gate in their market plus room in the issuer that would receive their capital,
 *  so a person it is not open to never reaches a refusal by clicking. */
export function canStartContract(
  product: Pick<Product, 'contract_template_version_id' | 'terms_available' | 'is_active'>,
  gate: string,
  holdsProduct: boolean,
): boolean {
  return (
    !!product.contract_template_version_id
    && gate === 'approved'
    && !holdsProduct
    && product.terms_available
    && product.is_active
  );
}

/** True when the Series' terms are known but it is not open to this person, who does not hold it
 *  already. When the terms are unknown the hero's own "Terms unavailable" line says so instead. */
export function isClosedToPerson(
  product: Pick<Product, 'terms_available' | 'is_active'>,
  holdsProduct: boolean,
): boolean {
  return product.terms_available && !product.is_active && !holdsProduct;
}

/** Said in place of the contribution step when the Series is not open to this person. */
export function NotOpenNote(): JSX.Element {
  return (
    <p role="note" className="text-sm mb-3" style={{ color: colors.inkSoft, lineHeight: 1.5 }}>
      This series is not open to new contributions from you at this time. Your Account Executive can tell you about the series that are.
    </p>
  );
}

/** "Talk to your advisor" CTA — copy + style follow the callback state. */
function AdvisorButton({
  callbackState,
  productName,
  onClick,
}: {
  readonly callbackState: CallbackUiState;
  readonly productName: string;
  readonly onClick: () => void;
}): JSX.Element {
  const isRequested = callbackState.kind === 'requested';
  const isSubmitting = callbackState.kind === 'submitting';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isSubmitting || isRequested}
      style={{
        width: '100%',
        padding: '14px 18px',
        borderRadius: 10,
        border: 'none',
        fontSize: 14,
        fontWeight: 700,
        cursor: isSubmitting || isRequested ? 'default' : 'pointer',
        background: isRequested ? colors.successBg : gradients.gold,
        color: isRequested ? colors.success : colors.navy,
        marginBottom: 12,
      }}
    >
      {isSubmitting && 'Requesting…'}
      {callbackState.kind === 'idle' && `Talk to your Account Executive about ${productName} →`}
      {isRequested && (
        <>
          ✓ {callbackState.alreadyExisted ? 'Your Account Executive already has this request' : 'Your Account Executive has been notified'}
          {callbackState.advisorName && !callbackState.alreadyExisted
            ? ` — ${callbackState.advisorName} will reach out`
            : ''}
        </>
      )}
      {callbackState.kind === 'error' && 'Try again'}
    </button>
  );
}

/** "Invest in this product" CTA — copy, style and disabled state follow the
 *  suitability (#58) and product-document (#59) checks. Extracted from the page
 *  component to keep its cognitive complexity under Sonar's ceiling. */
function InvestButton({
  productName,
  suitability,
  kycCheck,
  isInvesting,
  onInvest,
}: {
  readonly productName: string;
  readonly suitability: SuitabilityCheckResult | null;
  readonly kycCheck: ProductKycCheckResult | null;
  readonly isInvesting: boolean;
  readonly onInvest: () => void;
}): JSX.Element {
  // GitLab #58: when the suitability check has loaded and returned
  // not-ok, render the button disabled with a tooltip listing the
  // missing requirements. While the check is still loading we leave
  // the button enabled (the server-side gate will 403 if needed).
  const suitabilityBlocked = suitability !== null && !suitability.ok;
  // #59: product-specific document requirements — same posture as
  // suitability (loaded => can block; loading => leave enabled, the
  // server-side origination gate is the boundary).
  const kycBlocked = kycCheck !== null && !kycCheck.ok;
  const suitabilityTooltip = suitabilityBlocked
    ? suitability.missing_requirements
        .map((c) => suitabilityService.describeRequirement(c))
        .join('\n')
    : undefined;
  return (
    <button
      type="button"
      onClick={onInvest}
      disabled={isInvesting || suitabilityBlocked || kycBlocked}
      title={suitabilityTooltip}
      aria-describedby={suitabilityBlocked ? 'suitability-block-reason' : undefined}
      style={{
        width: '100%',
        padding: '12px 18px',
        borderRadius: 10,
        border: `2px solid ${suitabilityBlocked ? colors.border : colors.navy}`,
        fontSize: 14,
        fontWeight: 700,
        cursor: isInvesting || suitabilityBlocked || kycBlocked ? 'not-allowed' : 'pointer',
        background: colors.surface,
        color: suitabilityBlocked || kycBlocked ? colors.muted : colors.navy,
        marginBottom: 12,
        opacity: suitabilityBlocked || kycBlocked ? 0.7 : 1,
      }}
    >
      {isInvesting && 'Opening contract…'}
      {!isInvesting && suitabilityBlocked && `Not a fit yet — talk to your Account Executive`}
      {!isInvesting && !suitabilityBlocked && kycBlocked && 'Documents needed — update your file below'}
      {!isInvesting && !suitabilityBlocked && !kycBlocked && `Contribute to ${productName} →`}
    </button>
  );
}

/** #59 checklist panel: the product's extra document requirements and
 *  whether each is currently satisfied. Live/ratchet — a lapsed document
 *  reappears here (and re-disables Invest) immediately. The CTA opens or
 *  reuses a re-documentation case and deep-links into the existing
 *  onboarding surface (max reuse). Extracted from the page component to
 *  keep its cognitive complexity under Sonar's ceiling. */
function KycRequirementsPanel({
  kycCheck,
  productName,
  refreshBusy,
  refreshError,
  onUpdateDocuments,
}: {
  readonly kycCheck: ProductKycCheckResult;
  readonly productName: string;
  readonly refreshBusy: boolean;
  readonly refreshError: string | null;
  readonly onUpdateDocuments: () => void;
}): JSX.Element {
  return (
    <div
      className="rounded-lg p-3 text-xs mb-3"
      style={{
        background: kycCheck.ok ? colors.successBg : colors.infoBg,
        color: kycCheck.ok ? colors.success : colors.navy,
        border: `1px solid ${colors.border}`,
      }}
    >
      <p className="font-semibold mb-1">
        {kycCheck.ok
          ? `Your documents for ${productName} are up to date`
          : `${productName} requires additional documentation:`}
      </p>
      <ul className="pl-1 space-y-0.5" style={{ listStyle: 'none' }}>
        {kycCheck.requirements.map((r) => (
          <li key={r.category}>
            {r.current ? '✓' : '•'} {productKycService.describeCategory(r.category)}
            {r.current ? '' : ' — missing or expired'}
          </li>
        ))}
      </ul>
      {!kycCheck.ok && (
        <button
          type="button"
          onClick={onUpdateDocuments}
          disabled={refreshBusy}
          className="mt-2 font-bold underline"
          style={{
            color: colors.navy,
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: refreshBusy ? 'default' : 'pointer',
            textUnderlineOffset: 3,
          }}
        >
          {refreshBusy ? 'Opening…' : 'Update my documents →'}
        </button>
      )}
      {refreshError && (
        <p className="mt-1" style={{ color: colors.danger }}>{refreshError}</p>
      )}
    </div>
  );
}

export default function ProductDetail(): JSX.Element {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  // Set when the client arrived from a proposal (0070): starting the contract
  // accepts that proposal.
  const [searchParams] = useSearchParams();
  const proposalId = searchParams.get('proposal');
  const gate = useClientApprovalGate();
  const membershipApproved = useMembershipApproved(gate === 'approved');
  const { product, loadError, notFound, suitability, kycCheck } = useProductDetailData(
    productId,
    gate,
    navigate,
  );
  const holdsProduct = useHoldsProduct(productId, gate);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [callbackState, setCallbackState] = useState<CallbackUiState>({ kind: 'idle' });
  const [isInvesting, setIsInvesting] = useState(false);
  const [investError, setInvestError] = useState<string | null>(null);
  // The code of that refusal: one that sends the client to their Account Executive is
  // shown with the call action (S-43), never as a dead end.
  const [investErrorCode, setInvestErrorCode] = useState<string | null>(null);
  // S-31: a U.S. series needs the client's tax form first; the refusal says so and links to it.
  const [taxFormNeeded, setTaxFormNeeded] = useState(false);
  // Reg D accreditation modal (only US persons ever hit the gate that opens it).
  const [showAccreditation, setShowAccreditation] = useState(false);
  // Offering document (PPM) flow — acknowledged mirrors the server fact and
  // flips optimistically after a successful acknowledge call.
  const [offeringBusy, setOfferingBusy] = useState(false);
  const [offeringAcked, setOfferingAcked] = useState<boolean | null>(null);
  const [offeringError, setOfferingError] = useState<string | null>(null);

  async function handleDownloadOffering(): Promise<void> {
    if (!product || offeringBusy) return;
    setOfferingError(null);
    setOfferingBusy(true);
    try {
      // The server records delivery evidence for every issued link, so this
      // runs only on the explicit click.
      const dl = await catalogService.getOfferingDownload(product.id);
      setOfferingAcked(dl.acknowledged);
      window.open(dl.download_url, '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      setOfferingError(getApiErrorMessage(e, "Couldn't fetch the offering document. Please try again."));
    } finally {
      setOfferingBusy(false);
    }
  }

  async function handleAcknowledgeOffering(): Promise<void> {
    if (!product || offeringBusy) return;
    setOfferingError(null);
    setOfferingBusy(true);
    try {
      await catalogService.acknowledgeOffering(product.id);
      setOfferingAcked(true);
    } catch (e: unknown) {
      setOfferingError(getApiErrorMessage(e, "Couldn't record your acknowledgement. Please try again."));
    } finally {
      setOfferingBusy(false);
    }
  }

  async function handleRequestCallback(): Promise<void> {
    if (!product || callbackState.kind === 'submitting') return;
    setCallbackState({ kind: 'submitting' });
    try {
      const result = await callbacksService.requestCallback({
        product_id: product.id,
        context_note: `From the series page. Series: ${product.name}.`,
      });
      setCallbackState({
        kind: 'requested',
        advisorName: formatAdvisorName(result),
        alreadyExisted: result.already_existed,
      });
    } catch (e: unknown) {
      setCallbackState({
        kind: 'error',
        message: getApiErrorMessage(e, "Couldn't reach your Account Executive right now. Please try again."),
      });
    }
  }

  async function handleUpdateDocuments(): Promise<void> {
    if (refreshBusy) return;
    setRefreshError(null);
    setRefreshBusy(true);
    try {
      // Returns the client's active case or spawns a re-documentation case
      // inheriting their data (idempotent server-side).
      const { case_id } = await productKycService.startRefreshCase();
      navigate(`/client/onboarding/${case_id}`);
    } catch (e: unknown) {
      setRefreshError(getApiErrorMessage(e, "Couldn't open the document update flow. Please try again."));
      setRefreshBusy(false);
    }
  }

  async function handleInvest(): Promise<void> {
    if (!product?.contract_template_version_id || isInvesting) return;
    setInvestError(null);
    setInvestErrorCode(null);
    setTaxFormNeeded(false);
    setIsInvesting(true);
    try {
      // Idempotent server-side — if the client already has an open
      // contract case for this template version, returns that id
      // rather than spawning a duplicate.
      const { case_id } = await contractsService.startCase(
        product.contract_template_version_id,
      );
      if (proposalId) {
        try {
          await proposalsService.accept(proposalId, case_id);
        } catch {
          // Deliberately not blocking: the contract has started, which is what
          // the client asked for. The proposal simply stays open on the
          // dashboard, so nothing on screen becomes untrue.
        }
      }
      navigate(`/client/contracts/${case_id}`);
    } catch (e: unknown) {
      if (isAccreditationRequired(e)) {
        // Reg D gate: answer with the certification screen, then auto-retry.
        setShowAccreditation(true);
        setIsInvesting(false);
        return;
      }
      setTaxFormNeeded(isTaxFormRequired(e));
      setInvestErrorCode(getApiErrorCode(e));
      setInvestError(deriveInvestErrorMessage(e));
      setIsInvesting(false);
    }
  }

  if (notFound) {
    return (
      <>
        <PageHeader title="Series" />
        <NotAvailableNote />
      </>
    );
  }

  if (loadError) {
    return (
      <>
        <PageHeader title="Series" />
        <div
          className="rounded-lg p-4 text-sm"
          style={{
            background: colors.dangerBg,
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
          }}
          role="alert"
        >
          {loadError}
        </div>
      </>
    );
  }

  if (!product) {
    return (
      <>
        <PageHeader title="Series" />
        <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>
      </>
    );
  }

  const yieldPct = product.target_yield
    ? `${(Number.parseFloat(product.target_yield) * 100).toFixed(2)}%`
    : '—';
  // Term, redemption and early withdrawal through the shared derivation
  // (lib/redemptionTerms — audit #138): the detail endpoint carries funds'
  // real lockup/notice/frequency + penalty, so this page shows the policy
  // the client is actually buying.
  const term = product.terms_available ? formatTerm(product) : '—';
  const redemption = formatRedemption(product) ?? '—';
  const earlyWithdrawal = formatEarlyWithdrawal(product);
  const minFormatted = product.min_investment
    ? formatCents(product.min_investment)
    : null;
  const minInvestLabel =
    minFormatted && product.currency_code ? `${product.currency_code} ${minFormatted}` : '—';
  const risk = product.risk_level == null ? null : RISK_LEVELS[product.risk_level];
  const showFundLink = gate === 'approved';
  // "Invest in this product" only when all of: the product has an active
  // published per-product contract template, the client has cleared base
  // KYC, funds could actually answer with terms to invest on (0073), and the
  // server says the Series is open to this person.
  // Pre-approval clients have to use the "Talk to your advisor" path; the
  // gate also hides the "How to fund" link for the same reason.
  // A holder adds to what they have (TopUpLink) instead of signing again.
  const canInvest = canStartContract(product, gate, holdsProduct);
  const closedToPerson = isClosedToPerson(product, holdsProduct);

  return (
    <>
      <PageHeader title={product.name} />

      {/* Hero — yield, term, risk, min — the at-a-glance specs */}
      <section
        className="rounded-2xl p-6 mb-6"
        style={{ background: gradients.navy, color: '#FFFFFF' }}
      >
        <div
          className="grid gap-6"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}
        >
          <Stat label="Annual series result, for information" value={yieldPct} valueColor={colors.gold} />
          {/* Audit #140: the management fee is shown right next to the yield
              it reduces — a configured 0 reads "None", never a missing stat. */}
          <Stat label="Management fee" value={formatManagementFee(product)} />
          <Stat label="Term" value={term} />
          <Stat label="Redemption" value={redemption} />
          <Stat
            label="Early withdrawal"
            value={earlyWithdrawal.text}
            valueColor={earlyWithdrawal.tone === 'danger' ? colors.danger : undefined}
          />
          <Stat
            label="Risk profile"
            value={risk?.label ?? '—'}
            valueColor={risk?.color ?? undefined}
          />
          <Stat
            label="Minimum contribution"
            value={minInvestLabel}
          />
        </div>
        {!product.terms_available && (
          <p
            role="note"
            style={{ margin: '16px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.7)' }}
          >
            Terms unavailable right now — please check back shortly or talk to your Account Executive.
          </p>
        )}
      </section>

      <RiskDisclaimer extra="What you receive is your share of what the series actually earns while your capital is invested." />
      <OfferingNotice />

      {/* Long description */}
      {product.description && (
        <section
          className="rounded-2xl p-6 mb-6"
          style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <h2
            className="text-xs font-bold uppercase tracking-widest mb-3"
            style={{ color: colors.inkSoft }}
          >
            About this series
          </h2>
          <p className="text-sm" style={{ color: colors.ink, lineHeight: 1.6 }}>
            {product.description}
          </p>
        </section>
      )}

      {/* Next-steps panel — same philosophy as the simulator */}
      <section
        className="rounded-2xl p-6"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <h2
          className="text-xs font-bold uppercase tracking-widest mb-4"
          style={{ color: colors.inkSoft }}
        >
          Next steps
        </h2>

        <AdvisorButton
          callbackState={callbackState}
          productName={product.name}
          onClick={handleRequestCallback}
        />

        {callbackState.kind === 'error' && (
          <p className="text-xs mb-3" style={{ color: colors.danger }}>
            {callbackState.message}
          </p>
        )}

        {holdsProduct && <TopUpLink />}

        {/* Secondary CTA: Invest in this product — only when there's
            an active per-product contract template AND the client
            has cleared base KYC. GitLab #58: disabled with tooltip
            when the suitability check has returned not-ok; the
            server-side gate still runs on click as a defence in
            depth. Idempotent server-side so a duplicate click during
            navigation can't spawn a second case. */}
        {canInvest && membershipApproved === false && <MembershipFirstNote />}

        {canInvest && membershipApproved !== false && (
          <InvestButton
            productName={product.name}
            suitability={suitability}
            kycCheck={kycCheck}
            isInvesting={isInvesting}
            onInvest={handleInvest}
          />
        )}

        {closedToPerson && <NotOpenNote />}

        {!closedToPerson && kycCheck && kycCheck.requirements.length > 0 && (
          <KycRequirementsPanel
            kycCheck={kycCheck}
            productName={product.name}
            refreshBusy={refreshBusy}
            refreshError={refreshError}
            onUpdateDocuments={handleUpdateDocuments}
          />
        )}

        {investError && channelsToExecutive(investErrorCode) && (
          <ExecutiveRefusal
            message={investError}
            productId={product.id}
            context={seriesCallContext('From the series page', product.name)}
          />
        )}
        {investError && !channelsToExecutive(investErrorCode) && (
          <p className="text-xs mb-3" style={{ color: colors.danger }}>
            {investError}
            {taxFormNeeded && (
              <>
                {' '}
                <Link to={taxFormPath(product.id)} style={{ color: colors.ink, fontWeight: 600 }}>
                  Review my tax form →
                </Link>
              </>
            )}
          </p>
        )}

        {product.offering_document && (
          <OfferingDocumentRow
            doc={product.offering_document}
            busy={offeringBusy}
            acknowledged={offeringAcked ?? product.offering_document.acknowledged}
            error={offeringError}
            onDownload={handleDownloadOffering}
            onAcknowledge={handleAcknowledgeOffering}
          />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {!product.offering_document && product.prospectus_url && (
            <a
              href={product.prospectus_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: colors.navy,
                textDecoration: 'underline',
                textUnderlineOffset: 3,
              }}
            >
              Read the prospectus ↗
            </a>
          )}
          {showFundLink && (
            <>
              {!product.offering_document && product.prospectus_url && (
                <span style={{ fontSize: 13, color: colors.border }}>·</span>
              )}
              <Link
                to="/client/fund"
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.textMuted,
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                }}
              >
                How to make a contribution
              </Link>
            </>
          )}
        </div>
      </section>

      {showAccreditation && (
        <AccreditationModal
          onCertified={() => {
            setShowAccreditation(false);
            void handleInvest();
          }}
          onClose={() => setShowAccreditation(false)}
        />
      )}
    </>
  );
}

interface OfferingRowProps {
  doc: OfferingDocumentSummary;
  busy: boolean;
  acknowledged: boolean;
  error: string | null;
  onDownload: () => void;
  onAcknowledge: () => void;
}

const offeringLinkStyle = (busy: boolean, color: string): CSSProperties => ({
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: 13,
  fontWeight: 600,
  color,
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  cursor: busy ? 'default' : 'pointer',
});

/** Managed offering-document (PPM) row: audited download + version-specific
 *  acknowledgement. Extracted from the page body to keep ProductDetail under
 *  the cognitive-complexity ceiling. */
function OfferingDocumentRow({
  doc,
  busy,
  acknowledged,
  error,
  onDownload,
  onAcknowledge,
}: Readonly<OfferingRowProps>): JSX.Element {
  const label = busy
    ? 'Preparing…'
    : `Offering document (${doc.doc_type.toUpperCase()}, v${doc.version}) ↓`;
  return (
    <div className="mb-3" style={{ fontSize: 13 }}>
      <button
        type="button"
        onClick={onDownload}
        disabled={busy}
        style={offeringLinkStyle(busy, colors.navy)}
      >
        {label}
      </button>
      {acknowledged ? (
        <span className="ml-3" style={{ color: colors.success }}>
          ✓ Acknowledged
        </span>
      ) : (
        <button
          type="button"
          onClick={onAcknowledge}
          disabled={busy}
          className="ml-3"
          style={offeringLinkStyle(busy, colors.textMuted)}
        >
          I have received and read it — acknowledge
        </button>
      )}
      {doc.legend && (
        <p className="mt-1" style={{ color: colors.muted, fontSize: 11, lineHeight: 1.5 }}>
          {doc.legend}
        </p>
      )}
      {error && (
        <p className="mt-1" style={{ color: colors.danger, fontSize: 12 }}>
          {error}
        </p>
      )}
    </div>
  );
}

interface StatProps {
  label: string;
  value: string;
  valueColor?: string;
}

/** Beyond this a value is a sentence (redemption, early withdrawal), not a figure. */
const STAT_FIGURE_MAX_CHARS = 16;

function Stat({ label, value, valueColor }: Readonly<StatProps>): JSX.Element {
  // A figure reads at a glance in the large size; a sentence in that size fills the hero.
  const isFigure = value.length <= STAT_FIGURE_MAX_CHARS;
  return (
    <div>
      <div
        className="text-[11px] font-semibold uppercase tracking-wider mb-1.5"
        style={{ color: 'rgba(255,255,255,0.55)' }}
      >
        {label}
      </div>
      <div
        className={isFigure ? 'text-xl font-bold' : 'text-sm font-semibold'}
        style={{ color: valueColor ?? '#FFFFFF', lineHeight: isFigure ? 1.15 : 1.45 }}
      >
        {value}
      </div>
    </div>
  );
}
